import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  ArrowLeft, Plus, Wrench, Loader2, Image as ImageIcon, FileText, X, CalendarCheck,
  CalendarPlus, Bell, Check,
} from "lucide-react";
import { toast } from "sonner";

const fileToDataUrl = (f) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(f);
  });

async function uploadFile(f) {
  const dataUrl = await fileToDataUrl(f);
  const { data } = await axios.post(`${API}/equipment/upload`, {
    filename: f.name, content_type: f.type || "application/octet-stream", data: dataUrl,
  });
  return data;
}

/* ---------- Add Equipment dialog ---------- */
const AddEquipmentDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({ name: "", make: "", model: "", year: "", vin_serial: "" });
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const reset = () => { setForm({ name: "", make: "", model: "", year: "", vin_serial: "" }); setImage(null); };

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    try { setImage(await uploadFile(f)); } catch { toast.error("Image upload failed"); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/equipment`, {
        ...form,
        image_url: image?.url || null,
        image_storage_path: image?.storage_path || null,
      });
      toast.success("Equipment added");
      reset(); onOpenChange(false); onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add equipment");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Equipment</DialogTitle>
          <DialogDescription>Add a machine to track service logs against.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Name (e.g. CAT 299D3)" value={form.name} onChange={set("name")} data-testid="equipment-name-input" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Make" value={form.make} onChange={set("make")} data-testid="equipment-make-input" />
            <Input placeholder="Model" value={form.model} onChange={set("model")} data-testid="equipment-model-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Year" value={form.year} onChange={set("year")} data-testid="equipment-year-input" />
            <Input placeholder="VIN / Serial #" value={form.vin_serial} onChange={set("vin_serial")} data-testid="equipment-vin-input" />
          </div>
          <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="equipment-image-input" />
          <button type="button" onClick={() => imgRef.current?.click()} className="w-full flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-3 hover:bg-gray-50 text-left" data-testid="equipment-image-pick">
            {image ? <Check className="w-5 h-5 text-emerald-600" /> : <ImageIcon className="w-5 h-5 text-gray-400" />}
            <span className="text-sm text-gray-700 truncate">{image ? image.filename : "Upload photo (optional)"}</span>
          </button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#00341d] hover:bg-[#002816]" data-testid="equipment-save-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Add Equipment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- New Log dialog ---------- */
const NewLogDialog = ({ open, onOpenChange, equipment, gcalConnected, onCreated }) => {
  const [form, setForm] = useState({ headline: "", miles: "", hours: "", notes: "" });
  const [attachments, setAttachments] = useState([]);
  const [reminderOn, setReminderOn] = useState(false);
  const [reminder, setReminder] = useState({ date: "", name: "" });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const reset = () => {
    setForm({ headline: "", miles: "", hours: "", notes: "" });
    setAttachments([]); setReminderOn(false); setReminder({ date: "", name: "" });
  };

  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (f.size > 25 * 1024 * 1024) { toast.error(`${f.name} is over 25 MB`); continue; }
      try { const a = await uploadFile(f); setAttachments((p) => [...p, a]); }
      catch { toast.error(`Upload failed: ${f.name}`); }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!form.headline.trim()) { toast.error("Headline is required"); return; }
    if (reminderOn && (!reminder.date || !reminder.name.trim())) { toast.error("Add a reminder date and name"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/equipment/${equipment.id}/logs`, {
        equipment_id: equipment.id,
        headline: form.headline,
        miles: form.miles ? Number(form.miles) : null,
        hours: form.hours ? Number(form.hours) : null,
        notes: form.notes.trim() || null,
        attachments,
        reminder: reminderOn ? { enabled: true, date: reminder.date, name: reminder.name.trim() } : null,
      });
      if (reminderOn && !data.calendar_synced) {
        toast.success("Log saved. Connect Google Calendar to add the reminder as an event.");
      } else {
        toast.success(reminderOn ? "Log saved + reminder added to calendar" : "Log saved");
      }
      reset(); onOpenChange(false); onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save log");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Log — {equipment?.name}</DialogTitle>
          <DialogDescription>Record service, hours, notes and files for this machine.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Headline (e.g. 250hr service)" value={form.headline} onChange={set("headline")} data-testid="log-headline-input" />
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" placeholder="Miles" value={form.miles} onChange={set("miles")} data-testid="log-miles-input" />
            <Input type="number" placeholder="Hours" value={form.hours} onChange={set("hours")} data-testid="log-hours-input" />
          </div>
          <textarea className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Notes" value={form.notes} onChange={set("notes")} data-testid="log-notes-input" />

          <input ref={fileRef} type="file" multiple className="hidden" onChange={pickFiles} data-testid="log-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-3 hover:bg-gray-50 text-left" data-testid="log-file-pick">
            <FileText className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-700">Attach files / images</span>
          </button>
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attachments.map((a, i) => (
                <div key={a.id} className="flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs">
                  <span className="truncate max-w-[140px]">{a.filename}</span>
                  <button type="button" onClick={() => setAttachments((p) => p.filter((_, idx) => idx !== i))}><X className="w-3 h-3 text-gray-500" /></button>
                </div>
              ))}
            </div>
          )}

          <label className="flex items-center gap-2 pt-1 cursor-pointer select-none" data-testid="log-reminder-toggle">
            <input type="checkbox" checked={reminderOn} onChange={(e) => setReminderOn(e.target.checked)} className="w-4 h-4" />
            <Bell className="w-4 h-4 text-[#00341d]" />
            <span className="text-sm font-medium">Add a reminder</span>
          </label>
          {reminderOn && (
            <div className="space-y-2 rounded-md border border-gray-200 p-3 bg-gray-50">
              <Input type="date" value={reminder.date} onChange={(e) => setReminder((p) => ({ ...p, date: e.target.value }))} data-testid="log-reminder-date" />
              <Input placeholder="Reminder name" value={reminder.name} onChange={(e) => setReminder((p) => ({ ...p, name: e.target.value }))} data-testid="log-reminder-name" />
              <p className="text-xs text-gray-500">
                {gcalConnected ? "Adds an event to your Google Calendar with a link to this log." : "Connect Google Calendar to add this as a calendar event."}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#d97706] hover:bg-[#b45309]" data-testid="log-save-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Save Log
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Page ---------- */
export default function EquipmentPage() {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [gcal, setGcal] = useState({ configured: false, connected: false });

  const selected = equipment.find((e) => e.id === selectedId) || null;

  const fetchEquipment = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/equipment`); setEquipment(data); }
    catch { toast.error("Could not load equipment"); }
  }, []);

  const fetchGcal = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/gcal/status`); setGcal(data); } catch { /* ignore */ }
  }, []);

  const fetchLogs = useCallback(async (id) => {
    if (!id) { setLogs([]); return; }
    setLoadingLogs(true);
    try { const { data } = await axios.get(`${API}/equipment/${id}/logs`); setLogs(data); }
    catch { toast.error("Could not load logs"); }
    finally { setLoadingLogs(false); }
  }, []);

  useEffect(() => { fetchEquipment(); fetchGcal(); }, [fetchEquipment, fetchGcal]);
  useEffect(() => { fetchLogs(selectedId); }, [selectedId, fetchLogs]);

  const connectGcal = async () => {
    try {
      const { data } = await axios.get(`${API}/gcal/connect`);
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Google Calendar is not configured yet");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-[#00341d] text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="text-white hover:bg-white/10" data-testid="equipment-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Wrench className="w-6 h-6 text-emerald-300" />
              <h1 className="font-heading text-2xl font-bold">Equipment Logs</h1>
            </div>
          </div>
          {gcal.connected ? (
            <span className="flex items-center gap-2 text-sm text-emerald-200" data-testid="gcal-connected">
              <CalendarCheck className="w-4 h-4" /> Calendar connected
            </span>
          ) : (
            <Button variant="outline" onClick={connectGcal} className="border-white/30 text-white hover:bg-white/10" data-testid="gcal-connect-button">
              <CalendarPlus className="w-4 h-4 mr-2" /> Connect Google Calendar
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Equipment selector */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-8">
          <div className="flex-1">
            <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Select equipment</label>
            <select
              className="w-full h-11 rounded-md border border-input bg-white px-3 text-sm"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              data-testid="equipment-select"
            >
              <option value="">— Choose a machine —</option>
              {equipment.map((e) => (
                <option key={e.id} value={e.id}>{e.name}{e.make ? ` · ${e.make}` : ""}</option>
              ))}
            </select>
          </div>
          <Button variant="outline" onClick={() => setAddOpen(true)} className="h-11 border-[#00341d]/30 text-[#00341d]" data-testid="add-equipment-button">
            <Plus className="w-4 h-4 mr-2" /> Add Equipment
          </Button>
        </div>

        {/* Selected equipment + logs */}
        {selected ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                {selected.image_url ? (
                  <img src={selected.image_url} alt={selected.name} className="w-16 h-16 rounded-md object-cover border" />
                ) : (
                  <div className="w-16 h-16 rounded-md bg-gray-100 flex items-center justify-center"><Wrench className="w-7 h-7 text-gray-400" /></div>
                )}
                <div>
                  <div className="font-bold text-lg text-gray-900">{selected.name}</div>
                  <div className="text-sm text-gray-500">
                    {[selected.make, selected.model, selected.year].filter(Boolean).join(" · ")}
                    {selected.vin_serial ? ` · ${selected.vin_serial}` : ""}
                  </div>
                </div>
              </div>
              <Button onClick={() => setLogOpen(true)} className="bg-[#d97706] hover:bg-[#b45309]" data-testid="new-log-button">
                <Plus className="w-4 h-4 mr-2" /> New Log
              </Button>
            </div>

            {loadingLogs ? (
              <div className="py-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : logs.length === 0 ? (
              <div className="py-12 text-center text-gray-500 border-2 border-dashed rounded-lg">
                No logs yet. Tap <span className="font-semibold">New Log</span> to add the first one.
              </div>
            ) : (
              <div className="space-y-3" data-testid="logs-list">
                {logs.map((log) => (
                  <div key={log.id} className="bg-white rounded-lg border border-gray-200 p-4" data-testid={`log-item-${log.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="font-semibold text-gray-900">{log.headline}</div>
                      <div className="text-xs text-gray-400">{new Date(log.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="text-sm text-gray-500 mt-1 flex gap-4">
                      {log.hours != null && <span>{log.hours} hrs</span>}
                      {log.miles != null && <span>{log.miles} mi</span>}
                    </div>
                    {log.notes && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{log.notes}</p>}
                    {log.attachments?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {log.attachments.map((a) => a.is_image ? (
                          <a key={a.id} href={a.url} target="_blank" rel="noreferrer">
                            <img src={a.url} alt={a.filename} className="w-16 h-16 rounded object-cover border" />
                          </a>
                        ) : (
                          <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">
                            <FileText className="w-3 h-3" /> {a.filename}
                          </a>
                        ))}
                      </div>
                    )}
                    {log.reminder?.enabled && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-[#00341d]">
                        <Bell className="w-3 h-3" />
                        <span>{log.reminder.name} · {log.reminder.date}</span>
                        {log.reminder.calendar_event_link && (
                          <a href={log.reminder.calendar_event_link} target="_blank" rel="noreferrer" className="underline">View in Calendar</a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="py-16 text-center text-gray-500 border-2 border-dashed rounded-lg">
            Select a machine above, or add one to get started.
          </div>
        )}
      </div>

      <AddEquipmentDialog open={addOpen} onOpenChange={setAddOpen} onCreated={fetchEquipment} />
      {selected && (
        <NewLogDialog open={logOpen} onOpenChange={setLogOpen} equipment={selected} gcalConnected={gcal.connected} onCreated={() => fetchLogs(selectedId)} />
      )}
    </div>
  );
}
